import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { TabsGenerator } from "../../Common";

const getStoredToken = () => {
  try {
    return JSON.parse(window.localStorage.getItem("current_user"))?.token || null;
  } catch (_) {
    return null;
  }
};

const postAuth = (iframeWindow, theme) => {
  const token = getStoredToken();
  if (!iframeWindow || !token) return;
  iframeWindow.postMessage(
    {
      type: "dmvi:auth",
      token,
      apiBaseUrl: import.meta.env.VITE_SERVICE_URL,
      // Same primary/secondary colors applied to this app's own MUI theme
      // (App.jsx's createTheme(state.userReducer.theme)) — relayed as-is so
      // embedded ext-apps match instead of guessing hex values.
      primaryColor: theme?.palette?.primary?.main,
      secondaryColor: theme?.palette?.primary?.dark,
    },
    window.location.origin
  );
};

// Measures real remaining space down to the Layout's footer instead of a
// hardcoded viewport calc, since padding/footer height shift with
// sidebar-collapse and fullscreen mode.
const ExternalAppFrame = ({ src }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const [height, setHeight] = useState("100vh");
  // While the ext-app has a modal open we dim the whole screen (like a native dialog)
  // WITHOUT moving/resizing the page: the iframe is pinned at its current rect above a
  // full-viewport dim overlay, so the sidebar/header grey out but the page stays put.
  const [modalOpen, setModalOpen] = useState(false);
  const [frameRect, setFrameRect] = useState(null);
  const theme = useSelector((state) => state.userReducer.theme);

  useLayoutEffect(() => {
    if (modalOpen && containerRef.current) {
      const r = containerRef.current.getBoundingClientRect();
      setFrameRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setFrameRect(null);
    }
  }, [modalOpen]);

  useEffect(() => {
    const recalc = () => {
      const el = containerRef.current;
      if (!el) return;
      const footer = document.querySelector(".footer");
      const top = el.getBoundingClientRect().top;
      const bottom = footer
        ? footer.getBoundingClientRect().top
        : window.innerHeight;
      setHeight(`${Math.max(bottom - top, 200)}px`);
    };
    recalc();
    const timeoutId = setTimeout(recalc, 100);
    window.addEventListener("resize", recalc);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", recalc);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      // "ready" is the reliable handshake: the iframe's onLoad can fire before its own
      // React tree has mounted a message listener, so a post sent purely on onLoad can be
      // dropped silently (postMessage doesn't queue for late listeners). The child announces
      // once it's actually listening, and we reply here instead of relying on timing.
      if (event.data?.type === "dmvi:ready") {
        postAuth(iframeRef.current?.contentWindow, theme);
      }
      // Posted back if the ext-app's own token refresh fails (fully expired/invalid) —
      // re-send whatever's currently in localStorage (pro-frontend's own axios interceptor
      // keeps it fresh independently of this iframe's activity).
      if (event.data?.type === "dmvi:auth-expired") {
        postAuth(iframeRef.current?.contentWindow, theme);
      }
      // The ext-app opened/closed a modal — expand the iframe to full viewport so its
      // dim overlay covers the whole screen (sidebar/header included), then restore.
      if (event.data?.type === "dmvi:modal") {
        setModalOpen(!!event.data.open);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [theme]);

  // Pin the iframe at its measured rect (not fullscreen) so the page doesn't reflow;
  // raise it above the dim overlay so the ext-app's own modal stays visible.
  const containerStyle =
    modalOpen && frameRect
      ? {
          position: "fixed",
          top: frameRect.top,
          left: frameRect.left,
          width: frameRect.width,
          height: frameRect.height,
          zIndex: 1300,
        }
      : { width: "100%", height };

  return (
    <>
      {modalOpen &&
        createPortal(
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1299 }} />,
          document.body
        )}
      <div ref={containerRef} style={containerStyle}>
        <iframe
          ref={iframeRef}
          src={src}
          title="external-app"
          onLoad={(e) => postAuth(e.target.contentWindow, theme)}
          style={{ border: 0, display: "block", width: "100%", height: "100%" }}
        />
      </div>
    </>
  );
};

const ExternalApp = ({ src, routeObj }) => {
  const label = routeObj?.name || "App";
  const value = routeObj?.app_slug || "external-app";

  return (
    <TabsGenerator
      tabsList={[{ label, value, component: <ExternalAppFrame src={src} /> }]}
      activeTabs={value}
    />
  );
};

export default ExternalApp;
